import { $ } from "../lib/dom";
import { back, canControl, interactAhead, pressDir, releaseDir } from "./player";
import type { Dir } from "./types";
import { closeZoom, isZoomOpen, zoomKey } from "./zoom";

/** Fraction of the pad radius treated as "no direction". */
const DEAD_ZONE = 0.22;

const ARROW: Record<Dir, string> = { up: "ArrowUp", down: "ArrowDown", left: "ArrowLeft", right: "ArrowRight" };

/** Direction under the finger: dominant axis from the pad centre. Sliding changes direction without lifting. */
function dirAt(el: HTMLElement, e: PointerEvent): Dir | null {
  const r = el.getBoundingClientRect();
  const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
  const dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
  if (Math.hypot(dx, dy) < DEAD_ZONE) return null;
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? "right" : "left";
  return dy > 0 ? "down" : "up";
}

/**
 * While a close-up is open the D-pad first offers the direction to it (the
 * laparoscope pans with arrows); otherwise it closes the close-up and walks.
 */
function startDir(d: Dir): void {
  if (isZoomOpen()) {
    if (zoomKey(new KeyboardEvent("keydown", { key: ARROW[d] }))) return;
    closeZoom();
  }
  pressDir(d);
}

function initDpad(): void {
  const pad = $("#dpad");
  let cur: Dir | null = null;
  const set = (d: Dir | null): void => {
    if (d === cur) return;
    if (cur) releaseDir(cur);
    cur = d;
    pad.dataset.dir = d ?? "";
    if (d && canControl()) {
      startDir(d);
      // Safari has no Vibration API.
      if ("vibrate" in navigator) navigator.vibrate(6);
    }
  };
  pad.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    pad.setPointerCapture(e.pointerId);
    set(dirAt(pad, e));
  });
  pad.addEventListener("pointermove", (e) => {
    if (pad.hasPointerCapture(e.pointerId)) set(dirAt(pad, e));
  });
  for (const ev of ["pointerup", "pointercancel", "lostpointercapture"] as const)
    pad.addEventListener(ev, () => set(null));
}

function tapButton(el: HTMLElement, action: () => void): void {
  el.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    el.classList.add("on");
    if (canControl()) action();
  });
  for (const ev of ["pointerup", "pointercancel", "pointerleave"] as const)
    el.addEventListener(ev, () => el.classList.remove("on"));
}

let lastHint = "";

/** Label on the A button: what pressing it would interact with. */
export function setPadHint(text: string): void {
  if (text === lastHint) return;
  lastHint = text;
  $("#padHint").textContent = text;
  $("#padA").classList.toggle("ready", !!text);
}

/** On-screen D-pad + A/B for touch devices (shown via CSS on coarse pointers only). */
export function initTouchPad(): void {
  initDpad();
  tapButton($("#padA"), () => {
    if (!isZoomOpen()) interactAhead();
  });
  tapButton($("#padB"), back);
}
